import { useMemo, useRef, useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Star, MapPin, ChevronRight, CheckCircle, LocateFixed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Customer map — Leaflet + Google road tiles (same look as the detail-page map), mirror of the iOS
// CustomerRestaurantMapView. 🌎 globe markers, same marker style per
// business; tap a pin → detail card at the bottom (name, type, rating,
// distance) → card opens the business detail page.
//
// restaurants must already be island + active + acceptingOrders filtered.

const ISLAND_CENTERS = {
  'St. Kitts': { center: [17.33379, -62.75197], zoom: 12 },
  'Nevis': { center: [17.1554, -62.5796], zoom: 13 },
};

// Business-type emoji per pin — same mapping as iOS emojiForRestaurantType.
const TYPE_EMOJI = {
  'Fast Food': '🍔', 'Bar & Grill': '🍖', 'Pizza': '🍕', 'KFC': '🍗',
  'BBQ Grill': '🍖', 'Seafood Restaurant': '🦐', 'Cafe': '☕',
  'Beauty Supply': '💅', 'Retail Store': '🛍️', 'Farmer': '🥬',
  'Supermarket': '🛒', 'Fish & Meat': '🐟', 'Asian': '🍜', 'Indian': '🍛',
  'Bakery': '🥐', 'Juice Stand': '🧃', 'Craft & Souvenirs': '🎨',
  'Liquor Store & Smoke Shop': '🍷', 'Roti/Patty': '🫓',
  'Perfume Store': '🧴', 'Jewelry Store': '💎', 'Electronics Store': '📱',
  'Home Goods': '🏠', 'Home Goods / Hardware Store': '🏠', 'Water Supply': '💧',
  'Vegetarian': '🥗', 'Snack Boxes': '🍱', 'Car Parts': '🔧',
  'Pharmacy': '💊', 'Latin': '🌮', 'Mexican': '🌶️', 'Sushi': '🍣',
  'Steak House': '🥩', 'Local Restaurant': '🍽️',
};

function typeEmoji(type) {
  return TYPE_EMOJI[type] || '📍';
}

function pinIcon(restaurant, selected) {
  return L.divIcon({
    className: '',
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px)">
        <div style="width:40px;height:40px;border-radius:50%;background:rgba(34,197,94,0.3);
          border:2px solid ${selected ? '#16A34A' : '#22C55E'};
          display:flex;align-items:center;justify-content:center;font-size:22px;
          box-shadow:0 2px 4px rgba(0,0,0,0.3)">${typeEmoji(restaurant.restaurantType)}</div>
      </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 34],
  });
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function coordsOf(restaurant) {
  const c = restaurant.coordinates;
  if (!c) return null;
  const lat = c.latitude ?? c._lat;
  const lon = c.longitude ?? c._long;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  return [lat, lon];
}

// Fixes Leaflet tile rendering inside a modal that mounts hidden/animated.
function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

export default function RestaurantMapModal({ restaurants = [], island = 'St. Kitts', businessType = 'food', isFoodType, onClose }) {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [category, setCategory] = useState(businessType); // 'all' | 'food' | 'retail'
  const [tapped, setTapped] = useState(null);
  const [userPos, setUserPos] = useState(null);

  const isFood = isFoodType || (() => true);

  const visible = useMemo(() => {
    const withCoords = restaurants.filter(r => coordsOf(r));
    if (category === 'food') return withCoords.filter(r => isFood(r.restaurantType));
    if (category === 'retail') return withCoords.filter(r => !isFood(r.restaurantType));
    return withCoords;
  }, [restaurants, category]);

  const initial = ISLAND_CENTERS[island] || ISLAND_CENTERS['St. Kitts'];

  const locateMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const latlng = [pos.coords.latitude, pos.coords.longitude];
      setUserPos(latlng);
      mapRef.current?.flyTo(latlng, 16, { duration: 0.8 });
    });
  };

  const distanceFor = (restaurant) => {
    if (!userPos) return null;
    const c = coordsOf(restaurant);
    if (!c) return null;
    const miles = haversineKm(userPos[0], userPos[1], c[0], c[1]) * 0.621371;
    return miles < 0.1 ? `${Math.round(miles * 5280)} ft away` : `${miles.toFixed(1)} mi away`;
  };

  const pill = (value, label) => (
    <button
      key={value}
      onClick={() => { setCategory(value); setTapped(null); }}
      className={`flex-1 text-xs font-medium py-1.5 rounded-lg ${category === value ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-800'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-white flex flex-col">
      {/* Header — X left, island name centered */}
      <div className="relative flex items-center justify-center h-12 border-b border-gray-200 shrink-0">
        <button onClick={onClose} className="absolute left-3 text-gray-500">
          <X size={22} />
        </button>
        <span className="text-[15px] font-semibold">{island}</span>
      </div>

      {/* Category pills */}
      <div className="flex gap-2 px-4 py-2 shrink-0">
        {pill('all', 'All')}
        {pill('food', 'Food')}
        {pill('retail', 'Retail')}
      </div>

      <div className="relative flex-1">
        <MapContainer
          ref={mapRef}
          center={initial.center}
          zoom={initial.zoom}
          className="absolute inset-0"
          zoomControl={true}
        >
          <InvalidateOnMount />
          <TileLayer
            attribution="Map data &copy;2026 Google"
            url="https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
            subdomains={['0', '1', '2', '3']}
            maxZoom={20}
          />
          {userPos && (
            <Marker
              position={userPos}
              icon={L.divIcon({
                className: '',
                html: '<div style="width:18px;height:18px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>',
                iconSize: [18, 18],
                iconAnchor: [9, 9],
              })}
            />
          )}
          {visible.map(restaurant => (
            <Marker
              key={restaurant.id}
              position={coordsOf(restaurant)}
              icon={pinIcon(restaurant, tapped?.id === restaurant.id)}
              eventHandlers={{ click: () => setTapped(restaurant) }}
            />
          ))}
        </MapContainer>

        {/* Locate me */}
        <button
          onClick={locateMe}
          className="absolute top-3 right-3 z-[1000] w-11 h-11 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg"
        >
          <LocateFixed size={20} />
        </button>

        {/* Business detail card — appears when a pin is tapped */}
        {tapped && (
          <button
            onClick={() => navigate(`/restaurant/${tapped.id}?businessType=${businessType || 'food'}`)}
            className="absolute inset-x-3 bottom-3 z-[1000] bg-white/95 rounded-2xl shadow-xl px-3 py-3 flex items-center gap-2.5 text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-bold text-gray-900 truncate">{tapped.name}</span>
                {tapped.isVerified && <CheckCircle size={13} className="text-[#30B0C7] shrink-0" />}
              </div>
              <div className="text-[12px] text-gray-500 truncate">{tapped.restaurantType}</div>
              <div className="flex items-center gap-2 mt-0.5">
                {tapped.rating > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Star size={10} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-[11px] font-semibold text-gray-800">{Number(tapped.rating).toFixed(1)}</span>
                  </span>
                )}
                {distanceFor(tapped) && (
                  <span className="flex items-center gap-0.5 text-[11px] text-gray-500">
                    <MapPin size={9} />
                    {distanceFor(tapped)}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight size={22} className="text-red-500 shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
}
